import { useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiAvatar,
  MiBadge,
  MiModal,
  MiWorkOrderCard,
} from "@miplatform/components";
import {
  ButtonVariants,
  Colors,
  IAttemptListItemResponseForWorkOrder,
  ICalendarSet,
  IUserListItemResponse,
} from "@miplatform/types";
import {
  getDate,
  getInitials,
  getMaxDate,
  getRootUrl,
} from "@miplatform/utils";
import { mdiAccountPlusOutline } from "@mdi/js";
import { Box, Checkbox, Typography } from "@mui/material";
import { EmptyTransferListView } from "../../../../../components/TransferList/components/EmptyTransferListView";
import { useCalendarContext } from "../../../../../providers";
import { useAssignFromFollowUpModalViewModel } from "./useAssignFromFollowUpModalViewModel";

const getAvailabilityColors = (
  time: number,
  palette: ReturnType<typeof useStyles>["theme"]["palette"],
  capacityWarningThreshold: number,
  capacityCriticalThreshold: number
) => {
  if (time <= capacityWarningThreshold && time > capacityCriticalThreshold) {
    return {
      background: palette.orange.light,
      foreground: palette.orange.darkAccent,
    };
  }
  if (time <= capacityCriticalThreshold) {
    return { background: palette.red.light, foreground: palette.red.dark };
  }
  return { background: palette.green.light, foreground: palette.green.dark };
};

interface AssignFromFollowUpModalProps {
  open: boolean;
  onClose: () => void;
  user: IUserListItemResponse;
  calendarSet: ICalendarSet | null;
  assignedAttempts: IAttemptListItemResponseForWorkOrder[];
  followUpAttempts: IAttemptListItemResponseForWorkOrder[];
}

export const AssignFromFollowUpModal = ({
  open,
  onClose,
  user,
  calendarSet,
  assignedAttempts,
  followUpAttempts,
}: AssignFromFollowUpModalProps) => {
  const { classes, theme } = useStyles();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { refreshData } = useCalendarContext();

  const handleClose = () => {
    setSelectedIds([]);
    onClose();
  };

  const {
    isLoading,
    assignableUsersAvailableTime,
    getAssignableUsersAvailableTime,
    bulkAssignAttempts,
  } = useAssignFromFollowUpModalViewModel({
    onClose: handleClose,
    refreshData,
  });
  const availableTime = useMemo(
    () =>
      assignableUsersAvailableTime.find((u) => u.id === user.id)
        ?.availableTime || null,
    [assignableUsersAvailableTime, user.id]
  );
  const displayName =
    user?.displayName ?? `${user?.firstName} ${user?.lastName}`;
  const profilePictureRefId = user?.profilePicture?.referenceId;

  const capacityWarningThreshold = calendarSet?.capacityWarningThreshold || 3;
  const capacityCriticalThreshold = calendarSet?.capacityCriticalThreshold || 1;
  const profilePictureUrl = profilePictureRefId
    ? `${getRootUrl()}/${profilePictureRefId}`
    : null;

  const availability = useMemo(
    () =>
      getAvailabilityColors(
        availableTime || 0,
        theme.palette,
        capacityWarningThreshold,
        capacityCriticalThreshold
      ),
    [
      availableTime,
      theme.palette,
      capacityWarningThreshold,
      capacityCriticalThreshold,
    ]
  );
  const availabilityText = useMemo(() => {
    if (availableTime === null) {
      return "--";
    }
    if (availableTime < 1) {
      const minutes = Math.round(availableTime * 60);
      return `${minutes}m Available`;
    }
    const hours = Math.floor(availableTime);
    const minutes = Math.round((availableTime - hours) * 60);
    if (minutes === 60) {
      return `${hours + 1}h Available`;
    }
    if (minutes === 0) {
      return `${hours}h Available`;
    }
    return `${hours}h ${minutes}m Available`;
  }, [availableTime]);

  const handleToggle = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleSubmit = () => {
    const now = getDate();

    const lastAssignedEnd = assignedAttempts.reduce((latest, current) => {
      const currentEnd = getDate(current.scheduledEndDate).local();

      return currentEnd.isAfter(latest) ? currentEnd : latest;
    }, now);

    let currentTimeCursor = getMaxDate(now, lastAssignedEnd);

    const scheduledFollowUps = followUpAttempts
      .filter((attempt) => selectedIds.includes(attempt.id))
      .map((attempt) => {
        const duration = attempt.expectedDuration || 60;

        const newStart = currentTimeCursor;
        const newEnd = currentTimeCursor.clone().add(duration, "minutes");
        currentTimeCursor = newEnd;

        return {
          ...attempt,
          userId: user.id,
          attemptId: attempt.id,
          scheduledStartDate: newStart.toISOString(),
          scheduledEndDate: newEnd.toISOString(),
        };
      });

    bulkAssignAttempts(scheduledFollowUps);
  };

  useEffect(() => {
    getAssignableUsersAvailableTime();
  }, []);

  return (
    <MiModal
      open={open}
      closeModal={handleClose}
      width={448}
      titleIcon={mdiAccountPlusOutline}
      leftIconColor={theme.palette.primary.main}
      title="Assign From Follow-Up"
      description={`Choose which followup work orders you’d like to assign to ${displayName} today.`}
      loading={isLoading}
      actions={[
        {
          name: "Cancel",
          variant: ButtonVariants.Text,
          color: Colors.Black,
          onClick: onClose,
        },
        {
          name: "Add Work Orders",
          variant: ButtonVariants.Contained,
          color: Colors.Primary,
          disabled:
            isLoading || !followUpAttempts.length || !selectedIds.length,
          onClick: handleSubmit,
        },
      ]}
    >
      <Box className={classes.root}>
        <Box className={classes.userHeaderContainer}>
          <Box className={classes.userNameContainer}>
            <MiAvatar
              className={classes.userNameAvatar}
              data-testid="user-initials"
              src={profilePictureUrl}
            >
              {getInitials(displayName)}
            </MiAvatar>
            <Typography variant="HD1" className={classes.userNameText}>
              {displayName}
            </Typography>
          </Box>
          {typeof availableTime === "number" && (
            <Box>
              <MiBadge
                className={classes.badge}
                backgroundColor={availability.background}
                color={availability.foreground}
                borderColor={availability.foreground}
                text={availabilityText}
                fontSize={theme.fontSize.xsmall}
              />
            </Box>
          )}
        </Box>
        <Box className={classes.workOrdersContainer}>
          {followUpAttempts.map((attempt) => {
            const isSelected = selectedIds.includes(attempt.id);
            const duration = (attempt.expectedDuration || 0) / 60;
            const isDisabled =
              typeof availableTime === "number"
                ? !isSelected && duration > availableTime
                : false;

            return (
              <Box
                key={attempt.id}
                className={classes.attemptContainer}
                sx={{ opacity: isDisabled ? 0.5 : 1 }}
              >
                <Checkbox
                  checked={isSelected}
                  disabled={isDisabled}
                  onChange={() => handleToggle(attempt.id)}
                />
                <MiWorkOrderCard
                  className={classes.workOrderCard}
                  attempt={attempt}
                  onCardClick={() => handleToggle(attempt.id)}
                />
              </Box>
            );
          })}
          {followUpAttempts.length === 0 && (
            <EmptyTransferListView
              message="No follow-up work orders available."
              minHeight={197}
            />
          )}
        </Box>
      </Box>
    </MiModal>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
  },
  userHeaderContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    border: `1px solid ${theme.palette.grey.lightAccent}`,
    backgroundColor: theme.palette.grey.mediumLight,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
  },
  userNameContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(1),
    overflow: "hidden",
  },
  userNameText: {
    color: theme.palette.bluegrey.dark,
    textWrap: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
  },
  userNameAvatar: {
    width: 16,
    height: 16,
    fontSize: theme.fontSize.xxsmall,
  },
  attemptContainer: {
    display: "flex",
  },
  workOrdersContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    maxHeight: 197,
    overflowY: "auto",
  },
  workOrderCard: {
    width: "100%",
  },
  badge: {
    flex: 1,
    height: 14,
    alignItems: "center",
    justifyContent: "center",
  },
}));
